import React, { useState, useEffect, useCallback } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  LinearProgress,
  CircularProgress,
} from "@material-ui/core";
import { Alert } from "@material-ui/lab";
import { makeStyles, useTheme } from "@material-ui/core/styles";
import {
  Warning,
  Delete,
  Chat,
  Person,
  Message,
  Label,
  Dashboard,
  CheckCircle,
} from "@material-ui/icons";
import api from "../../services/api";

const useStyles = makeStyles((theme) => ({
  dialog: {
    "& .MuiDialog-paper": {
      minWidth: "500px",
      maxWidth: "600px",
      borderRadius: "12px",
      padding: theme.spacing(2),
      backgroundColor: theme.mode === 'dark' ? '#1f2937' : theme.palette.background.paper,
      color: theme.palette.text.primary,
    },
  },
  title: {
    textAlign: "center",
    color: theme.mode === 'dark' ? '#ef4444' : "#f44336",
    fontWeight: "bold",
    marginBottom: theme.spacing(2),
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  content: {
    padding: theme.spacing(2),
    backgroundColor: theme.mode === 'dark' ? '#1f2937' : theme.palette.background.paper,
  },
  warningBox: {
    backgroundColor: theme.mode === 'dark' ? '#78350f' : "#fff3e0",
    border: `1px solid ${theme.mode === 'dark' ? '#f59e0b' : '#ff9800'}`,
    borderRadius: "8px",
    padding: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
  warningIcon: {
    color: theme.mode === 'dark' ? '#f59e0b' : "#ff9800",
    marginRight: theme.spacing(1),
  },
  dataList: {
    backgroundColor: theme.mode === 'dark' ? '#374151' : "#f5f5f5",
    borderRadius: "8px",
    padding: theme.spacing(1),
    marginBottom: theme.spacing(2),
    border: `1px solid ${theme.mode === 'dark' ? '#4B5563' : '#e0e0e0'}`,
  },
  listItem: {
    padding: theme.spacing(0.5, 0),
    color: theme.palette.text.primary,
  },
  listIcon: {
    minWidth: "32px",
    color: theme.mode === 'dark' ? '#9ca3af' : "#666",
  },
  progressContainer: {
    margin: theme.spacing(2, 0),
  },
  progressBar: {
    height: "6px",
    borderRadius: "3px",
    backgroundColor: theme.mode === 'dark' ? '#374151' : "#e0e0e0",
    "& .MuiLinearProgress-bar": {
      borderRadius: "3px",
      backgroundColor: "#f44336",
    },
  },
  actions: {
    justifyContent: "center",
    padding: theme.spacing(2),
    gap: theme.spacing(1),
    backgroundColor: theme.mode === 'dark' ? '#1f2937' : theme.palette.background.paper,
    borderTop: `1px solid ${theme.mode === 'dark' ? '#4B5563' : theme.palette.divider}`,
  },
  cancelButton: {
    backgroundColor: theme.mode === 'dark' ? '#4B5563' : "#757575",
    color: "white",
    "&:hover": {
      backgroundColor: theme.mode === 'dark' ? '#6B7280' : "#616161",
    },
  },
  deleteButton: {
    backgroundColor: "#f44336",
    color: "white",
    "&:hover": {
      backgroundColor: "#d32f2f",
    },
  },
  successMessage: {
    color: theme.mode === 'dark' ? '#10b981' : "#4caf50",
    fontWeight: "bold",
    textAlign: "center",
    marginTop: theme.spacing(2),
  },
  loadingContainer: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    padding: theme.spacing(3),
    color: theme.palette.text.primary,
  },
  countValue: {
    fontWeight: "bold",
    color: theme.mode === 'dark' ? '#ef4444' : "#f44336",
    marginLeft: theme.spacing(1),
  },
  statsBox: {
    marginTop: theme.spacing(2),
    padding: theme.spacing(1.5),
    backgroundColor: theme.mode === 'dark' ? '#374151' : "#f5f5f5",
    borderRadius: "8px",
    border: `1px solid ${theme.mode === 'dark' ? '#4B5563' : '#e0e0e0'}`,
  },
}));

const DeleteWhatsAppModal = ({
  open,
  onClose,
  whatsAppName = "WhatsApp",
  whatsAppId = null,
  onConfirm,
  onDeleteComplete,
}) => {
  const classes = useStyles();
  const theme = useTheme();
  const [showProgress, setShowProgress] = useState(false);
  const [progress, setProgress] = useState(0);
  const [isComplete, setIsComplete] = useState(false);
  const [hasError, setHasError] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [deleteStats, setDeleteStats] = useState(null);
  const [previewStats, setPreviewStats] = useState(null);
  const [loadingPreview, setLoadingPreview] = useState(false);

  // ✅ FUNCIÓN PARA CARGAR PREVIEW DE ELIMINACIÓN - Optimizada con useCallback
  const loadDeletePreview = useCallback(async () => {
    if (!whatsAppId) return;
    
    try {
      setLoadingPreview(true);
      const response = await api.get(`/whatsapp/${whatsAppId}/delete-preview`);
      setPreviewStats(response.data.deletePreview);
      console.log("📊 Preview de eliminación cargado:", response.data.deletePreview);
    } catch (error) {
      console.error("❌ Error cargando preview de eliminación:", error);
      setPreviewStats({
        ticketsCount: 0,
        contactsCount: 0,
        messagesCount: 0,
        tagsCount: 0,
        ticketTrackingsCount: 0,
        userRatingsCount: 0
      });
    } finally {
      setLoadingPreview(false);
    }
  }, [whatsAppId]);

  useEffect(() => {
    if (open) {
      // Resetear estado al abrir modal
      setProgress(0);
      setIsComplete(false);
      setHasError(false);
      setErrorMessage("");
      setDeleteStats(null);
      setShowProgress(false);
      setPreviewStats(null);
      setLoadingPreview(false);
      
      // ✅ CARGAR PREVIEW DE ELIMINACIÓN
      if (whatsAppId) {
        loadDeletePreview();
      }
    }
  }, [open, whatsAppId, loadDeletePreview]);

  const handleClose = (event, reason) => {
    // ✅ NUEVA API: Filtrar eventos de backdrop click cuando no se puede cerrar
    if (reason === 'backdropClick' && !isComplete && !hasError) {
      return; // No cerrar si está en proceso
    }
    
    if (isComplete && onDeleteComplete) {
      onDeleteComplete(); // Si la eliminación está completa, forzar recarga al cerrar
    }
    onClose(); // Siempre cerrar el modal
  };

  // ✅ FUNCIÓN PARA EJECUTAR ELIMINACIÓN MANUAL AL CONFIRMAR
  const handleConfirmDelete = async () => {
    if (!whatsAppId || !onConfirm) return;

    try {
      setShowProgress(true);
      setProgress(20);

      // ✅ LLAMAR API PARA ELIMINACIÓN COMPLETA
      const response = await onConfirm(whatsAppId);
      
      if (response && response.deletedStats) {
        setDeleteStats(response.deletedStats);
        console.log("📊 Estadísticas de eliminación:", response.deletedStats);
      }

      setProgress(100);
      setIsComplete(true);

      // ✅ NOTIFICAR AL PADRE PARA ACTUALIZAR LA PÁGINA
      if (onDeleteComplete) {
        setTimeout(() => {
          onDeleteComplete();
        }, 2000); // Esperar 2 segundos antes de notificar al padre
      }

    } catch (err) {
      console.error("Error al eliminar WhatsApp:", err);
      setHasError(true);
      setErrorMessage(err.response?.data?.error || err.response?.data?.message || "Error desconocido al eliminar la conexión.");
      setProgress(100);
    }
  };

   const dataToDelete = [
     { 
       icon: <Chat />, 
       text: "Tickets y conversaciones",
       count: previewStats?.ticketsCount || 0
     },
     { 
       icon: <Person />, 
       text: "Contactos asociados",
       count: previewStats?.contactsCount || 0
     },
     { 
       icon: <Message />, 
       text: "Mensajes y archivos",
       count: previewStats?.messagesCount || 0
     },
     { 
       icon: <Label />, 
       text: "Etiquetas asociadas",
       count: previewStats?.tagsCount || 0
     },
     { 
       icon: <Dashboard />, 
       text: "Estadísticas del dashboard",
       count: previewStats?.ticketTrackingsCount || 0
     },
     { 
       icon: <CheckCircle />, 
       text: "Evaluaciones y ratings",
       count: previewStats?.userRatingsCount || 0
     },
   ];

  return (
     <Dialog
       open={open}
       onClose={handleClose}
       className={classes.dialog}
       disableEscapeKeyDown={!isComplete && !hasError}
     >
      <DialogTitle className={classes.title}>
        <Warning style={{ fontSize: "32px", marginRight: "8px" }} />
        Eliminar Conexión WhatsApp
      </DialogTitle>

       <DialogContent className={classes.content}>
         {!showProgress ? (
           <>
             <Box className={classes.warningBox}>
               <Typography variant="h6" style={{ color: theme.mode === 'dark' ? '#f59e0b' : "#ff9800", marginBottom: "8px" }}>
                 <Warning className={classes.warningIcon} />
                 ADVERTENCIA: Eliminación Completa
               </Typography>
               <Typography variant="body2" style={{ color: theme.palette.text.primary }}>
                 Esta acción eliminará permanentemente la conexión <strong>"{whatsAppName}"</strong> 
                 y <strong>TODOS los datos relacionados</strong>. Esta acción no se puede deshacer.
               </Typography>
             </Box>

             <Alert severity="error" style={{ marginBottom: "16px" }}>
               <strong>Se eliminarán los siguientes datos:</strong>
             </Alert>

             <Box className={classes.dataList}>
               {loadingPreview ? (
                 <Box className={classes.loadingContainer}>
                   <CircularProgress size={24} />
               <Typography variant="body2" style={{ marginLeft: "8px", color: theme.palette.text.primary }}>
                     Cargando conteos...
                   </Typography>
                 </Box>
               ) : (
                 <List dense>
                   {dataToDelete.map((item, index) => (
                     <ListItem key={index} className={classes.listItem}>
                       <ListItemIcon className={classes.listIcon}>
                         {item.icon}
                       </ListItemIcon>
                       <ListItemText 
                         primary={
                           <Box display="flex" justifyContent="space-between" alignItems="center">
                             <span>{item.text}</span>
                             <span className={classes.countValue}>
                               {item.count}
                             </span>
                           </Box>
                         }
                         primaryTypographyProps={{ variant: "body2" }}
                       />
                     </ListItem>
                   ))}
                 </List>
               )}
             </Box>

             <Alert severity="info">
               <strong>Recomendación:</strong> Si necesitas conservar el historial de conversaciones, 
               considera desconectar la sesión en lugar de eliminar la conexión.
             </Alert>
           </>
         ) : (
           <>
             <Box style={{ textAlign: "center", marginBottom: "16px" }}>
               <Delete style={{ fontSize: "48px", color: "#f44336", marginBottom: "8px" }} />
               <Typography variant="h6" style={{ color: "#f44336" }}>
                 {isComplete ? "Eliminación Completada" : "Eliminando " + whatsAppName + "..."}
               </Typography>
             </Box>

             <Box className={classes.progressContainer}>
               <LinearProgress
                 variant="determinate"
                 value={progress}
                 className={classes.progressBar}
               />
               <Typography variant="body2" style={{ textAlign: "center", marginTop: "8px", color: theme.palette.text.primary }}>
                 {Math.round(progress)}% completado
               </Typography>
             </Box>

             {isComplete && deleteStats && (
               <Box className={classes.statsBox}>
                 <Typography variant="subtitle2" gutterBottom style={{ color: theme.palette.text.primary, fontWeight: "bold" }}>
                   Estadísticas de Eliminación:
                 </Typography>
                 <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "4px" }}>
                   <Typography variant="body2" style={{ fontWeight: "bold", color: theme.palette.text.primary }}>
                     Tickets eliminados:
                   </Typography>
                   <Typography variant="body2" style={{ color: theme.palette.text.primary }}>
                     {deleteStats.deletedTickets}
                   </Typography>
                 </div>
                 <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "4px" }}>
                   <Typography variant="body2" style={{ fontWeight: "bold", color: theme.palette.text.primary }}>
                     Contactos eliminados:
                   </Typography>
                   <Typography variant="body2" style={{ color: theme.palette.text.primary }}>
                     {deleteStats.deletedContacts}
                   </Typography>
                 </div>
                 <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "4px" }}>
                   <Typography variant="body2" style={{ fontWeight: "bold", color: theme.palette.text.primary }}>
                     Mensajes eliminados:
                   </Typography>
                   <Typography variant="body2" style={{ color: theme.palette.text.primary }}>
                     {deleteStats.deletedMessages}
                   </Typography>
                 </div>
                 <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "4px" }}>
                   <Typography variant="body2" style={{ fontWeight: "bold", color: theme.palette.text.primary }}>
                     Etiquetas eliminadas:
                   </Typography>
                   <Typography variant="body2" style={{ color: theme.palette.text.primary }}>
                     {deleteStats.deletedTags}
                   </Typography>
                 </div>
                 <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "4px" }}>
                   <Typography variant="body2" style={{ fontWeight: "bold", color: theme.palette.text.primary }}>
                     Estadísticas eliminadas:
                   </Typography>
                   <Typography variant="body2" style={{ color: theme.palette.text.primary }}>
                     {deleteStats.deletedTicketTrackings}
                   </Typography>
                 </div>
                 <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "4px" }}>
                   <Typography variant="body2" style={{ fontWeight: "bold", color: theme.palette.text.primary }}>
                     Evaluaciones eliminadas:
                   </Typography>
                   <Typography variant="body2" style={{ color: theme.palette.text.primary }}>
                     {deleteStats.deletedUserRatings}
                   </Typography>
                 </div>
               </Box>
             )}

             {isComplete && (
               <Typography className={classes.successMessage}>
                 <CheckCircle style={{ marginRight: "8px" }} />
                 Eliminación completada exitosamente
               </Typography>
             )}

             {isComplete && (
               <Typography 
                 style={{ 
                   color: "#4caf50", 
                   fontWeight: "bold", 
                   marginTop: "16px",
                   textAlign: "center"
                 }}
               >
                 <span role="img" aria-label="checkmark">✅</span> Actualizando página automáticamente...
               </Typography>
             )}

             {hasError && errorMessage && (
               <Typography 
                 style={{ 
                   color: "#f44336", 
                   fontWeight: "bold", 
                   marginTop: "16px",
                   fontSize: "14px",
                   textAlign: "center"
                 }}
               >
                 <span role="img" aria-label="warning">⚠️</span> {errorMessage}
               </Typography>
             )}
           </>
         )}
      </DialogContent>

       <DialogActions className={classes.actions}>
         {!showProgress ? (
           <>
             <Button
               onClick={onClose}
               variant="contained"
               className={classes.cancelButton}
             >
               Cancelar
             </Button>
             <Button
               onClick={handleConfirmDelete}
               variant="contained"
               className={classes.deleteButton}
               startIcon={<Delete />}
             >
               Confirmar Eliminación
             </Button>
           </>
         ) : (
           <Button
             onClick={handleClose}
             variant="contained"
             className={classes.cancelButton}
             disabled={!isComplete && !hasError}
           >
             {isComplete ? "Cerrar" : hasError ? "Entendido" : "Eliminando..."}
           </Button>
         )}
       </DialogActions>
    </Dialog>
  );
};

export default DeleteWhatsAppModal;
